import React, { useState } from 'react';
import axios from 'axios';
import './GlobalStyles.css';
import ShippingLocationManager from './ShippingLocationManager';

const AdminTools = () => {
  const [shippingIds, setShippingIds] = useState('');
  const [resultMessage, setResultMessage] = useState('');

  // State for the Log Lookup
  const [upn, setUpn] = useState('');
  const [timeRange, setTimeRange] = useState('24h'); // '24h' or 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [logData, setLogData] = useState([]);
  const [accessLevel, setAccessLevel] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // Handle deleting shipping items
  const handleDelete = async (e) => {
    e.preventDefault();
    setResultMessage('');

    const ids = shippingIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== '');
    if (ids.length === 0) {
      setResultMessage('Please enter at least one shipping ID.');
      return;
    }

    try {
      for (const id of ids) {
        await axios.delete(`${apiBaseUrl}/api/shipping-items/${id}`);
      }
      setResultMessage(`Successfully deleted shipping items: ${ids.join(', ')}`);
    } catch (error) {
      console.error('Error deleting shipping items:', error);
      setResultMessage(`Error deleting shipping items: ${error.message}`);
    }
  };

  // Handle fetching user activity logs
  const handleLogLookup = async (e) => {
    e.preventDefault();
    try {
      const params = { upn };
      if (timeRange === '24h') {
        params.last24hours = true;
      } else {
        params.start = startDate;
        params.end = endDate;
      }

      const response = await axios.get(`${apiBaseUrl}/api/logs`, { params });
      // The back end returns only logs with actionType of INSERT, DOWNLOAD, or DELETE.
      setLogData(response.data);
      setCurrentPage(1); // Reset pagination
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  // Helper function to compute displayed action type.
  const getDisplayedActionType = (entry) => {
    const type = entry.message.actionType.toUpperCase();
    if (type === 'DOWNLOAD') {
      return entry.message.details.toLowerCase().includes('downloaded')
        ? 'Downloaded'
        : 'Viewed';
    }
    return type;
  };

  // Pagination calculations
  const totalEntries = logData.length;
  const totalPages = Math.ceil(totalEntries / pageSize);
  const displayedLogs = logData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Convert log data to CSV and trigger download
  const downloadCSV = () => {
    if (logData.length === 0) return;

    // Create CSV header
    const header = ['Timestamp','Action Type','Details','Route','Method','Duration','Status Code','Body','User'];
    // Map each log entry to a CSV row
    const rows = logData.map(entry => {
      const { timestamp } = entry;
      const { actionType, details, route, method, duration, statusCode, body, user } = entry.message;
      return [
        timestamp,
        actionType,
        details,
        route,
        method || '',
        duration || '',
        statusCode || '',
        body || '',
        user
      ];
    });

    // Build CSV string
    const csvContent = [
      header.join(','),
      ...rows.map(r => r.map(val => `"${val}"`).join(','))
    ].join('\n');

    // Create a blob and trigger a download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileName = `log_report_${new Date().toISOString().slice(0,10)}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="page-container">
      <div className="header-container">
        <h1>Admin Tools Dashboard</h1>
      </div>

      {/* Shipping Items Deletion Section */}
      <div className="admin-tools-container">
        <h2>Delete Shipping Items</h2>
        <p>Enter one or more Shipping IDs (separated by commas) to delete:</p>
        <form onSubmit={handleDelete} className="universal-form">
          <div className="universal-form-group">
            <label htmlFor="shipping-ids">Shipping IDs</label>
            <input
              id="shipping-ids"
              type="text"
              value={shippingIds}
              onChange={(e) => setShippingIds(e.target.value)}
              placeholder="e.g. SHIP001, SHIP002"
              className="admin-input"
            />
          </div>
          <button type="submit" className="admin-delete-button">
            Delete Shipping Items
          </button>
        </form>
        {resultMessage && <p className="admin-result-message">{resultMessage}</p>}
      </div>

      {/* Shipping Location Manager Section */}
      <div className="admin-tools-container">
        <ShippingLocationManager />
      </div>

      {/* User Log Lookup Section */}
      <div className="admin-tools-container">
        <h2>User Log Lookup</h2>
        <form onSubmit={handleLogLookup} className="universal-form admin-log-lookup-form">
          <div className="universal-form-group">
            <label htmlFor="upn">User Principal Name (UPN):</label>
            <input
              id="upn"
              type="text"
              value={upn}
              onChange={(e) => setUpn(e.target.value)}
              placeholder="john.doe@stress.com"
              className="admin-input"
            />
          </div>
          <div className="universal-form-group">
            <label htmlFor="timeRange">Time Range:</label>
            <select
              id="timeRange"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="admin-input"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {timeRange === 'custom' && (
            <div className="custom-date-range">
              <div className="universal-form-group">
                <label htmlFor="startDate">Start Date/Time:</label>
                <input
                  id="startDate"
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="admin-input"
                />
              </div>
              <div className="universal-form-group">
                <label htmlFor="endDate">End Date/Time:</label>
                <input
                  id="endDate"
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="admin-input"
                />
              </div>
            </div>
          )}
          <button type="submit" className="lookup-button">
            Lookup Activity
          </button>
        </form>

        {accessLevel && <h3>Access Level: {accessLevel}</h3>}

        {logData.length > 0 && (
          <>
            <button onClick={downloadCSV} className="global-download-button">
              Download CSV Report
            </button>
            <table className="admin-log-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Action Type</th>
                  <th>Details</th>
                  <th>Route</th>
                  <th>Method</th>
                  <th>Duration</th>
                  <th>Status Code</th>
                  <th>Body</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {displayedLogs.map((entry, idx) => (
                  <tr key={idx}>
                    <td>{entry.timestamp}</td>
                    <td>{getDisplayedActionType(entry)}</td>
                    <td>{entry.message.details}</td>
                    <td>{entry.message.route}</td>
                    <td>{entry.message.method || 'N/A'}</td>
                    <td>{entry.message.duration || 'N/A'}</td>
                    <td>{entry.message.statusCode || 'N/A'}</td>
                    <td>{entry.message.body || 'N/A'}</td>
                    <td>{entry.message.user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-controls">
              <button onClick={handlePrevPage} disabled={currentPage === 1}>
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </button>
              <label>
                Page Size:
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminTools;
